import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Spinner, Alert, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Axios from 'axios';
import QueryCard from '../components/QueryCard';
import QueryDetails from '../components/QueryDetails';
import '../styles/queriesManagement.css';
import { NSSC_YEAR, PREV_NSSC_YEARS } from '../data/config';

// Helper function to call backend API
const callAxios = async (method, endpoint, data) => {
    const config = {
        headers: {
            authorization: localStorage.getItem("nssc-admin-token"),
        },
    };

    if (method === "GET") {
        return Axios.get(
            process.env.REACT_APP_BACKEND_URL + endpoint + (data ? "?" + data : ""),
            config
        );
    } else if (method === "POST") {
        return Axios.post(
            process.env.REACT_APP_BACKEND_URL + endpoint,
            data,
            config
        );
    }
};

const QueriesManagement = () => {
    const [queries, setQueries] = useState([]);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [stats, setStats] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        assignedTo: '',
        page: 1,
        limit: 100,
        search: ''
    });
    const [pagination, setPagination] = useState({});
    // Year selector: 'current' shows this cycle's live queries (nssc<YEAR> queries
    // collection); a numeric year (e.g. 2025) shows archived rows from the
    // previousqueries collection — read-only.
    const CURRENT_YEAR = NSSC_YEAR;
    const ARCHIVED_YEARS = PREV_NSSC_YEARS;
    const [selectedYear, setSelectedYear] = useState('current');
    const isArchive = selectedYear !== 'current';
    
    // Sync configuration - max emails to fetch per sync
    const [syncLimit, setSyncLimit] = useState(() => {
        const saved = localStorage.getItem('nssc-sync-limit');
        return saved ? parseInt(saved) : 30;
    });
    const [tempSyncLimit, setTempSyncLimit] = useState(syncLimit); // For debouncing
    const [showSyncConfig, setShowSyncConfig] = useState(false);

    const teams = ['tech', 'events', 'publi', 'finance', 'gsecs'];
    const statuses = ['pending', 'assigned', 'resolved'];
    
    // Team email mappings
    const teamEmails = {
        tech: ['omdabhade.nssc@gmail.com', 'susrithat@gmail.com', 'tankalasusritha.nssc@gmail.com'],
        events: ['arnavgupta.nssc@gmail.com', 'shradha.nssc@gmail.com'],
        publi: ['mehedhi.nssc@gmail.com', 'vedantmalsure@gmail.com'],
        finance: ['anusheshjumale88@gmail.com'],
        gsecs: ['anayadixit.nssc@gmail.com', 'vedantmalsure@gmail.com']
    };

    // Get user role
    const getUserRole = async () => {
        const token = localStorage.getItem('nssc-admin-token');
        if (!token) {
            setUserRole(0);
            return;
        }
        try {
            const resp = await Axios.get(
                process.env.REACT_APP_BACKEND_URL + '/admin/user/role',
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );
            if (resp && resp.data && resp.data.status === 200) {
                setUserRole(Number(resp.data.role));
            } else {
                setUserRole(0);
            }
        } catch (err) {
            // treat errors as unauthorized / visitor
            setUserRole(0);
        }
    };

    // Fetch queries
    const fetchQueries = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();

            if (filters.status) queryParams.append('status', filters.status);
            if (filters.assignedTo) queryParams.append('assignedTo', filters.assignedTo);
            queryParams.append('page', filters.page);
            queryParams.append('limit', filters.limit);
            if (isArchive) queryParams.append('year', selectedYear);

            const endpoint = isArchive ? '/queries/previous' : '/queries';
            const response = await callAxios('GET', endpoint, queryParams.toString());
            
            if (response.data.status === 200) {
                setQueries(response.data.data.queries || []);
                setPagination(response.data.data.pagination || {});
            } else {
                toast.error(response.data.message || 'Failed to fetch queries');
            }
        } catch (error) {
            console.error('Error fetching queries:', error);
            toast.error('Failed to fetch queries');
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStats = async () => {
        try {
            const endpoint = isArchive
                ? '/queries/previous/stats/summary'
                : '/queries/stats/summary';
            const params = isArchive ? `year=${selectedYear}` : '';
            const response = await callAxios('GET', endpoint, params);

            if (response.data.status === 200) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // Sync emails from Gmail
    const handleSync = async () => {
        try {
            setSyncing(true);
            const response = await callAxios('GET', '/queries/sync', `limit=${syncLimit}`);
            
            if (response.data.status === 200) {
                toast.success(
                    `✅ Sync completed! New: ${response.data.data.new}`,
                    { autoClose: 5000 }
                );
                fetchQueries();
                fetchStats();
            } else {
                toast.error(response.data.message || 'Failed to sync queries');
            }
        } catch (error) {
            console.error('Error syncing queries:', error);
            
            // Check for timeout errors
            if (error.code === 'ECONNABORTED' || error.response?.status === 504) {
                toast.error(
                    `⏱️ Sync timeout! Try reducing the limit (current: ${syncLimit}). Recommended: 10-25 emails.`,
                    { autoClose: 8000 }
                );
            } else {
                toast.error('Failed to sync queries');
            }
        } finally {
            setSyncing(false);
        }
    };

    // Assign query to team
    const handleAssign = async (queryId, team) => {
        try {
            const response = await callAxios('POST', `/queries/${queryId}/assign`, { team });
            
            if (response.data.status === 200) {
                toast.success(`Query assigned to ${team} team`);
                fetchQueries();
                fetchStats();
                
                if (selectedQuery && selectedQuery._id === queryId) {
                    setSelectedQuery(response.data.data);
                }
            } else {
                toast.error(response.data.message || 'Failed to assign query');
            }
        } catch (error) {
            console.error('Error assigning query:', error);
            toast.error('Failed to assign query');
        }
    };

    // Send reply
    const handleReply = async (queryId, message) => {
        try {
            const response = await callAxios('POST', `/queries/${queryId}/reply`, { message });
            
            if (response.data.status === 200) {
                toast.success('Reply sent successfully');
                fetchQueries();
                
                if (selectedQuery && selectedQuery._id === queryId) {
                    // Refresh the selected query to get updated replies
                    const queryResponse = await callAxios('GET', `/queries/${queryId}`, '');
                    if (queryResponse.data.status === 200) {
                        setSelectedQuery(queryResponse.data.data);
                    }
                }
            } else {
                toast.error(response.data.message || 'Failed to send reply');
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            toast.error('Failed to send reply');
        }
    };

    // Resolve query
    const handleResolve = async (queryId) => {
        try {
            const response = await callAxios('POST', `/queries/${queryId}/resolve`, {});
            
            if (response.data.status === 200) {
                toast.success('Query marked as resolved');
                fetchQueries();
                fetchStats();
                
                if (selectedQuery && selectedQuery._id === queryId) {
                    setSelectedQuery(response.data.data);
                }
            } else {
                toast.error(response.data.message || 'Failed to resolve query');
            }
        } catch (error) {
            console.error('Error resolving query:', error);
            toast.error('Failed to resolve query');
        }
    };

    // Reopen query (mark resolved query as pending)
    const handleReopen = async (queryId) => {
        try {
            const response = await callAxios('POST', `/queries/${queryId}/reopen`, {});
            
            if (response.data.status === 200) {
                toast.success('Query reopened and marked as pending');
                fetchQueries();
                fetchStats();
                
                if (selectedQuery && selectedQuery._id === queryId) {
                    setSelectedQuery(response.data.data);
                }
            } else {
                toast.error(response.data.message || 'Failed to reopen query');
            }
        } catch (error) {
            console.error('Error reopening query:', error);
            toast.error('Failed to reopen query');
        }
    };

    // Send reminder to team
    const handleSendReminder = async (team) => {
        if (!teamEmails[team]) {
            toast.error('Invalid team selected');
            return;
        }

        try {
            toast.info(`Sending reminder to ${team} team...`, { autoClose: 2000 });
            
            const response = await callAxios('POST', '/queries/remind', { 
                team,
                emails: teamEmails[team]
            });
            
            if (response.data.status === 200) {
                const { recipientCount, pendingQueriesCount } = response.data.data;
                toast.success(
                    `✉️ Reminder sent successfully!\n${recipientCount} team member${recipientCount > 1 ? 's' : ''} notified about ${pendingQueriesCount} pending quer${pendingQueriesCount === 1 ? 'y' : 'ies'}`,
                    { autoClose: 5000 }
                );
            } else {
                toast.error(response.data.message || 'Failed to send reminder');
            }
        } catch (error) {
            console.error('Error sending reminder:', error);
            toast.error('Failed to send reminder. Please try again.');
        }
    };

    // Handle filter change
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: 1
        }));
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setFilters(prev => ({
            ...prev,
            page: newPage
        }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            status: '',
            assignedTo: '',
            page: 1,
            limit: 100,
            search: ''
        });
    };

    // Update sync limit
    const handleSyncLimitChange = (newLimit) => {
        const limit = Math.max(5, Math.min(100, parseInt(newLimit) || 30)); // Between 5-100
        setSyncLimit(limit);
        localStorage.setItem('nssc-sync-limit', limit.toString());
        toast.success(`Sync limit updated to ${limit} emails`, { autoClose: 2000 });
    };

    // Debounce sync limit input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (tempSyncLimit !== syncLimit) {
                handleSyncLimitChange(tempSyncLimit);
            }
        }, 800); // 800ms delay after user stops typing

        return () => clearTimeout(timer);
    }, [tempSyncLimit]);

    useEffect(() => {
        getUserRole();
    }, []);

    useEffect(() => {
        if (userRole !== null && (userRole === 2 || userRole === 4)) {
            fetchQueries();
            fetchStats();
        }
    }, [filters, userRole, selectedYear]);

    // Clear any selected query when switching between current/archived, since
    // its _id belongs to a different collection and the actions wouldn't apply.
    useEffect(() => {
        setSelectedQuery(null);
    }, [selectedYear]);

    // Show loading while checking user role
    if (userRole === null) {
        return (
            <Container fluid className="queries-management-container">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Checking access permissions...</p>
                </div>
            </Container>
        );
    }

    // Show unauthorized message for users without proper role
    if (userRole !== 2 && userRole !== 4) {
        return (
            <Container fluid className="queries-management-container">
                <Row className="justify-content-center mt-5">
                    <Col md={6}>
                        <Alert variant="danger">
                            <Alert.Heading>Access Denied</Alert.Heading>
                            <p>
                                You do not have permission to access the Queries Management section.
                                This section is only available to authorized team members.
                            </p>
                            <hr />
                            <p className="mb-0">
                                If you believe this is an error, please contact your administrator.
                            </p>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="queries-management-container">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <h2 className="queries-title mb-0">Participant Queries Management</h2>
                            <Form.Select
                                size="sm"
                                style={{ width: 'auto' }}
                                value={selectedYear}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSelectedYear(v === 'current' ? 'current' : parseInt(v));
                                    setFilters(prev => ({ ...prev, page: 1 }));
                                }}
                                title="Switch between the current cycle and archived years"
                            >
                                <option value="current">{CURRENT_YEAR} (Current)</option>
                                {ARCHIVED_YEARS.map(y => (
                                    <option key={y} value={y}>{y} (Archived)</option>
                                ))}
                            </Form.Select>
                        </div>
                        {/* Sync controls - Admin only (role 4), current year only */}
                        {userRole === 4 && !isArchive && (
                            <div className="d-flex gap-2 align-items-center">
                                {/* Sync Limit Configuration */}
                                <div className="d-flex align-items-center me-2">
                                    <small className="text-muted me-2">Sync Limit:</small>
                                    <Form.Control
                                        type="number"
                                        min="5"
                                        max="100"
                                        value={tempSyncLimit}
                                        onChange={(e) => setTempSyncLimit(e.target.value)}
                                        style={{ 
                                            width: '80px',
                                            borderColor: tempSyncLimit !== syncLimit ? '#ffc107' : ''
                                        }}
                                        size="sm"
                                        title="Number of emails to fetch per sync (5-100). Auto-saves 800ms after you stop typing."
                                    />
                                    <small className="text-muted ms-1">emails</small>
                                    {tempSyncLimit !== syncLimit && (
                                        <small className="text-warning ms-1" style={{ fontSize: '0.75rem' }}></small>
                                    )}
                                </div>
                                
                                {/* Sync Button */}
                                <Button 
                                    variant="primary" 
                                    onClick={handleSync}
                                    disabled={syncing}
                                >
                                    {syncing ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Syncing...
                                        </>
                                    ) : (
                                        'Sync from Gmail'
                                    )}
                                </Button>

                                {/* Team Reminder Dropdown */}
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-warning" size="md">
                                         Send Reminder
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Header>Send reminder to team</Dropdown.Header>
                                        {teams.map(team => (
                                            <Dropdown.Item
                                                key={team}
                                                onClick={() => handleSendReminder(team)}
                                            >
                                                <span className="text-capitalize">{team}</span>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {isArchive && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="secondary" className="mb-0 py-2">
                            Viewing archived <strong>{selectedYear}</strong> queries — read only. Sync, assign, reply, and resolve are disabled.
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Statistics Cards */}
            {stats && (
                <Row className="mb-4">
                    <Col md={2}>
                        <Card className="stat-card stat-total">
                            <Card.Body>
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total Queries</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-pending">
                            <Card.Body>
                                <div className="stat-value">{stats.pending}</div>
                                <div className="stat-label">Pending</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-assigned">
                            <Card.Body>
                                <div className="stat-value">{stats.assigned}</div>
                                <div className="stat-label">Assigned</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="stat-card stat-resolved">
                            <Card.Body>
                                <div className="stat-value">{stats.resolved}</div>
                                <div className="stat-label">Resolved</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="stat-card stat-teams">
                            <Card.Body>
                                <div className="stat-label mb-2">By Team</div>
                                <div className="teams-stats">
                                    {teams.map(team => (
                                        <Badge key={team} bg="info" className="me-2">
                                            {team}: {stats.byTeam[team]}
                                        </Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filters */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="filter-card">
                        <Card.Body>
                            <Row className="align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            {statuses.map(status => (
                                                <option key={status} value={status}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Assigned To</Form.Label>
                                        <Form.Select
                                            value={filters.assignedTo}
                                            onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                                        >
                                            <option value="">All Teams</option>
                                            {teams.map(team => (
                                                <option key={team} value={team}>
                                                    {team.charAt(0).toUpperCase() + team.slice(1)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Per Page</Form.Label>
                                        <Form.Select
                                            value={filters.limit}
                                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                        >
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Search</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search anything..."
                                            value={filters.search || ''}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={1}>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        className="w-100"
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content */}
            <Row>
                <Col md={selectedQuery ? 5 : 12}>
                    <Card className="queries-list-card">
                        <Card.Body>
                            <h5 className="mb-3">
                                Queries ({pagination.total || 0})
                            </h5>
                            
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : queries.length === 0 ? (
                                <Alert variant="info">
                                    No queries found. Try adjusting your filters or sync from Gmail.
                                </Alert>
                            ) : (() => {
                                const q = (filters.search || '').trim().toLowerCase();
                                const shown = q
                                    ? queries.filter((qy) =>
                                        Object.values(qy || {}).some((v) => {
                                            if (v === null || v === undefined) return false;
                                            if (typeof v === 'object') {
                                                try { return JSON.stringify(v).toLowerCase().includes(q); } catch (e) { return false; }
                                            }
                                            return String(v).toLowerCase().includes(q);
                                        })
                                    )
                                    : queries;
                                if (shown.length === 0) {
                                    return (
                                        <Alert variant="info">No queries match "{filters.search}".</Alert>
                                    );
                                }
                                return (
                                <>
                                    <div className="queries-list">
                                        {shown.map(query => (
                                            <QueryCard
                                                key={query._id}
                                                query={query}
                                                isSelected={selectedQuery && selectedQuery._id === query._id}
                                                onClick={() => setSelectedQuery(query)}
                                                onAssign={handleAssign}
                                                teams={teams}
                                                readOnly={isArchive}
                                            />
                                        ))}
                                    </div>
                                    
                                    {/* Pagination */}
                                    {pagination.pages > 1 && (
                                        <div className="pagination-controls mt-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={pagination.page === 1}
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                            >
                                                Previous
                                            </Button>
                                            <span className="mx-3">
                                                Page {pagination.page} of {pagination.pages}
                                            </span>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={pagination.page === pagination.pages}
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    )}
                                </>
                                );
                            })()}
                        </Card.Body>
                    </Card>
                </Col>

                {selectedQuery && (
                    <Col md={7}>
                        <QueryDetails
                            query={selectedQuery}
                            onClose={() => setSelectedQuery(null)}
                            onReply={handleReply}
                            onAssign={handleAssign}
                            onResolve={handleResolve}
                            onReopen={handleReopen}
                            teams={teams}
                            readOnly={isArchive}
                        />
                    </Col>
                )}
            </Row>
        </Container>
    );
};

export default QueriesManagement;
